import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { SearchBasedPdfHighlight } from './SearchBasedPdfHighlight';

// Configure pdf.js worker to use bundled version that matches react-pdf's pdfjs version
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Worker is configured globally in index.tsx - do not override here
console.log(`📄 HighlightedPdfViewer: Using global worker configuration`);
console.log(`📄 Detected PDF.js version: ${pdfjs.version}`);
console.log(`🔗 Worker URL: ${pdfjs.GlobalWorkerOptions.workerSrc}`);

export interface Citation {
  documentId: string;
  page: number;
  startOffset: number;
  endOffset: number;
  text: string;
}

interface HighlightedPdfViewerProps {
  documentId: string;
  citations: Citation[];
  authToken: string;
  onError?: (error: string) => void;
  /** Fired whenever the highlighter finishes searching for a citation, so the
   *  surrounding modal can surface the real match status (no more
   *  "Unverified match" when the highlighter actually found something). */
  onMatchInfo?: (info: { found: boolean; confidence: number; matchType?: string; positionCount: number }) => void;
}

/**
 * PDF Viewer that displays a PDF with embedded citation highlights
 * This approach eliminates coordinate transformation issues by embedding
 * highlights directly into the PDF using PDF annotations
 */
export const HighlightedPdfViewer: React.FC<HighlightedPdfViewerProps> = ({
  documentId,
  citations,
  authToken,
  onError,
  onMatchInfo,
}) => {
  // Debug log to see what citations we're receiving
  console.log('🔍 HighlightedPdfViewer received citations:', citations);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState(1); // Always start at page 1, then navigate to citation
  const [scale, setScale] = useState(1.2);
  const [useTextSearch] = useState(true); // Enable text search by default
  const [retryKey, setRetryKey] = useState(0); // Force retry
  const [currentCitationIndex, setCurrentCitationIndex] = useState(0); // Track current citation
  // Ref to the scrolling PDF container so we can auto-scroll a found highlight
  // into view when the matcher reports its position.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Keep callback props in refs so the load effect doesn't re-fire (and
  // re-fetch / re-create the blob URL) just because a parent passed a new
  // inline lambda on every render.
  const onErrorRef = useRef(onError);
  const onMatchInfoRef = useRef(onMatchInfo);
  onErrorRef.current = onError;
  onMatchInfoRef.current = onMatchInfo;

  // Load PDF only when document changes. Cleanup is colocated with the effect
  // that creates the blob so React 18 StrictMode double-invocation can't
  // revoke a blob the next render is still trying to read (Vite exposed the
  // race that CRA's slower dev pipeline used to mask).
  useEffect(() => {
    if (!documentId || !authToken) return;

    console.log(`📄 useEffect triggered for document: ${documentId}`);

    let cancelled = false;
    let createdUrl: string | null = null;
    const controller = new AbortController();

    const loadPdf = async () => {
      try {
        setLoading(true);
        setError(null);

        const pdfResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/api/documents/${documentId}/stream`,
          {
            headers: { Authorization: `Bearer ${authToken}` },
            signal: controller.signal,
          }
        );

        if (!pdfResponse.ok) {
          throw new Error(`Failed to load PDF: ${pdfResponse.statusText}`);
        }

        const pdfBlob = await pdfResponse.blob();
        if (cancelled) return;
        console.log(`📄 Loaded PDF blob: ${pdfBlob.size} bytes, type: ${pdfBlob.type}`);

        createdUrl = URL.createObjectURL(pdfBlob);
        console.log(`🔗 Created blob URL for document ${documentId}`);

        setPdfUrl(createdUrl);
        setLoading(false);
      } catch (err) {
        if (cancelled || (err instanceof DOMException && err.name === 'AbortError')) return;
        const errorMessage = err instanceof Error ? err.message : 'Failed to load PDF';
        console.error('❌ PDF loading failed:', errorMessage);
        setError(errorMessage);
        setLoading(false);
        onErrorRef.current?.(errorMessage);
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
      controller.abort();
      if (createdUrl) {
        URL.revokeObjectURL(createdUrl);
      }
    };
    // Intentionally omit onError — accessed via ref so callers can pass inline
    // lambdas without triggering a reload of the PDF on every parent rerender.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, authToken, retryKey]);

  const handleRetry = () => {
    console.log('📄 Manual retry triggered');
    setRetryKey(prev => prev + 1);
  };

  // Add keyboard navigation for citations
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (citations.length <= 1) return;
      
      if (event.key === 'ArrowLeft' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        goToPrevCitation();
      } else if (event.key === 'ArrowRight' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        goToNextCitation();
      } else if (event.key === 'Home' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        navigateToCitation(0);
      } else if (event.key === 'End' && (event.ctrlKey || event.metaKey)) {
        event.preventDefault();
        navigateToCitation(citations.length - 1);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [citations, currentCitationIndex]);


  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    console.log(`✅ PDF loaded successfully: ${numPages} pages`);
    setNumPages(numPages);
    
    // Validate citation pages and auto-navigate to first valid citation
    if (citations.length > 0) {
      const validCitations = citations.filter(c => c.page >= 1 && c.page <= numPages);
      const invalidCitations = citations.filter(c => c.page < 1 || c.page > numPages);
      
      if (invalidCitations.length > 0) {
        console.warn(`⚠️ Found ${invalidCitations.length} citations with invalid page numbers:`);
        invalidCitations.forEach(c => {
          console.warn(`  - Citation page ${c.page} (document has ${numPages} pages): "${c.text?.substring(0, 50)}..."`);
        });
      }
      
      if (validCitations.length > 0) {
        const targetPage = validCitations[0].page;
        console.log(`📍 Auto-navigating to valid citation on page ${targetPage} of ${numPages}`);
        setPageNumber(targetPage);
        
        // Add a slight delay to ensure the page is rendered before showing the highlight indicator
        setTimeout(() => {
          // Scroll to center of viewport if needed
          const pageElement = document.querySelector('.react-pdf__Page');
          if (pageElement) {
            pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        }, 500);
      } else {
        console.error(`❌ No valid citations found! All citations reference pages beyond document range (${numPages} pages)`);
        setError(`Citation error: Referenced page ${citations[0].page} doesn't exist (document has ${numPages} pages)`);
      }
    }
  };

  const onDocumentLoadError = (error: Error) => {
    console.error('❌ PDF load error:', error);
    setError(`Failed to load PDF: ${error.message}`);
    onError?.(error.message);
  };

  const goToPrevPage = () => setPageNumber(page => Math.max(1, page - 1));
  const goToNextPage = () => setPageNumber(page => Math.min(numPages, page + 1));
  const zoomIn = () => setScale(scale => Math.min(3, scale + 0.2));
  const zoomOut = () => setScale(scale => Math.max(0.5, scale - 0.2));

  // Get citations on the current page
  const citationsOnCurrentPage = citations.filter(c => c.page === pageNumber);
  const totalCitationsOnPage = citationsOnCurrentPage.length;

  // Navigate to a specific citation
  const navigateToCitation = (index: number) => {
    if (index >= 0 && index < citations.length) {
      const citation = citations[index];
      setCurrentCitationIndex(index);
      setPageNumber(citation.page);
      
      // Scroll to the citation after page loads
      setTimeout(() => {
        const pageElement = document.querySelector('.react-pdf__Page');
        if (pageElement) {
          pageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 300);
    }
  };

  // Navigate between citations
  const goToPrevCitation = () => {
    const newIndex = currentCitationIndex > 0 ? currentCitationIndex - 1 : citations.length - 1;
    navigateToCitation(newIndex);
  };

  const goToNextCitation = () => {
    const newIndex = currentCitationIndex < citations.length - 1 ? currentCitationIndex + 1 : 0;
    navigateToCitation(newIndex);
  };

  // Check document status and word spans
  const checkDocumentStatus = async () => {
    try {
      console.log('🔍 Checking document status for:', documentId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/documents/${documentId}/word-spans/status`,
        {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (response.ok) {
        const status = await response.json();
        console.log('📊 Document status:', status);
        
        const message = `Document: ${status.documentName}
Status: ${status.documentStatus}
Word Spans: ${status.spanCount}
Has Word Spans: ${status.hasWordSpans ? 'Yes' : 'No'}
Extracted Text: ${status.extractedText} characters

${status.recommendations.join('\n')}

${status.sampleSpans.length > 0 ? 
  'Sample spans:\n' + status.sampleSpans.map((s: any) => `Page ${s.page}: "${s.text}" (${s.offsets})`).join('\n') : 
  'No sample spans available'
}`;
        
        alert(message);

        // If no word spans, offer to re-process
        if (!status.hasWordSpans) {
          const reprocess = window.confirm('This document has no word spans. Would you like to re-process it now to fix citation issues?');
          if (reprocess) {
            await reprocessDocument();
          }
        }
      } else {
        console.error('Failed to check document status:', response.status);
        alert('Failed to check document status. Please try again.');
      }
    } catch (error) {
      console.error('Error checking document status:', error);
      alert('Error checking document status. Please check the console for details.');
    }
  };

  // Re-process document to extract word spans
  const reprocessDocument = async () => {
    try {
      console.log('🔄 Re-processing document:', documentId);
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/documents/${documentId}/word-spans/reprocess`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        }
      );

      if (response.ok) {
        const result = await response.json();
        console.log('✅ Re-processing started:', result);
        alert(`Document queued for re-processing!\n\nDeleted ${result.deletedSpans} old spans.\nProcessing will happen in the background.\n\nRefresh the page in a few minutes to see the results.`);
      } else {
        console.error('Failed to re-process document:', response.status);
        const error = await response.json();
        alert(`Failed to re-process document: ${error.error}`);
      }
    } catch (error) {
      console.error('Error re-processing document:', error);
      alert('Error re-processing document. Please check the console for details.');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Generating highlighted PDF...</p>
          <p className="text-sm text-gray-500 mt-2">
            Processing {citations.length} citation{citations.length !== 1 ? 's' : ''}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center text-red-600">
          <div className="text-4xl mb-4">⚠️</div>
          <p className="text-lg font-semibold mb-2">Error Loading PDF</p>
          <p className="text-sm">{error}</p>
          <button
            onClick={handleRetry}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!pdfUrl) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-gray-600">No PDF available</p>
      </div>
    );
  }

  return (
    <div className="highlighted-pdf-viewer">
      {/* Add styles for text search highlights */}
      <style>{`
        @keyframes highlightPulse {
          0% { 
            box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
            transform: scale(1);
          }
          50% { 
            box-shadow: 0 4px 12px rgba(245, 158, 11, 0.6);
            transform: scale(1.02);
          }
          100% { 
            box-shadow: 0 2px 4px rgba(245, 158, 11, 0.3);
            transform: scale(1);
          }
        }
        
        .pdf-text-highlight {
          transition: all 0.2s ease;
        }
        
        .pdf-text-highlight:hover {
          background-color: rgba(255, 235, 59, 0.6) !important;
        }
        
        .react-pdf__Page__textLayer {
          z-index: 5 !important;
        }
      `}</style>
      {/* Controls */}
      <div className="flex items-center justify-between p-4 bg-gray-100 border-b">
        <div className="flex items-center space-x-4">
          <button
            onClick={goToPrevPage}
            disabled={pageNumber <= 1}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
          >
            ←
          </button>
          <span className="text-sm font-medium">
            Page {pageNumber} of {numPages}
          </span>
          <button
            onClick={goToNextPage}
            disabled={pageNumber >= numPages}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-300 transition-colors"
          >
            →
          </button>
        </div>

        <div className="flex items-center space-x-4">
          <button
            onClick={zoomOut}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            −
          </button>
          <span className="text-sm font-medium">
            {Math.round(scale * 100)}%
          </span>
          <button
            onClick={zoomIn}
            className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 transition-colors"
          >
            +
          </button>
        </div>

        <div className="flex items-center space-x-4">
          {/* Citation Navigation Controls */}
          {citations.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={goToPrevCitation}
                className="px-2 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                title="Previous citation"
              >
                ←
              </button>
              <span className="text-sm text-gray-600">
                Citation {currentCitationIndex + 1} of {citations.length}
              </span>
              <button
                onClick={goToNextCitation}
                className="px-2 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 transition-colors"
                title="Next citation"
              >
                →
              </button>
            </div>
          )}
          
          {citations.length > 0 && citations[currentCitationIndex].page !== pageNumber && (
            <button
              onClick={() => navigateToCitation(currentCitationIndex)}
              className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors animate-pulse"
              title={`Go to citation on page ${citations[currentCitationIndex].page}`}
            >
              📍 Go to Citation
            </button>
          )}
        </div>
      </div>

      {/* PDF Display */}
      <div
        ref={scrollContainerRef}
        className="pdf-container overflow-auto bg-gray-50"
        style={{ height: 'calc(100vh - 200px)' }}
      >
        <div className="flex justify-center p-4">
          <Document
            file={pdfUrl}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading={
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-gray-600">Loading PDF...</p>
              </div>
            }
          >
            <div style={{ position: 'relative' }}>
              <Page 
                pageNumber={pageNumber} 
                scale={scale}
                renderTextLayer={true} // Enable text layer for search
                renderAnnotationLayer={false} // Disable annotation layer since we're using text search
              />
              {/* Text-based highlights for quote citations */}
              {pdfUrl && citations
                .filter(citation => citation.page >= 1 && citation.page <= numPages)
                .map((citation, index) => (
                  <SearchBasedPdfHighlight
                    key={`search-highlight-${index}`}
                    pdfUrl={pdfUrl}
                    citation={citation}
                    pageNumber={pageNumber}
                    scale={scale}
                    onMatchInfo={onMatchInfo}
                    onFirstMatchPosition={(pos) => {
                      if (!pos) return;
                      // Scroll the highlight into view inside the scrolling
                      // PDF container. Coordinates are pre-scale (PDF space);
                      // multiply by `scale` to get pixel offsets.
                      const container = scrollContainerRef.current;
                      if (!container) return;
                      const targetY = pos.y * scale;
                      const containerH = container.clientHeight;
                      const padding = 80;
                      const desiredScrollTop = Math.max(0, targetY - padding);
                      // Only scroll if the highlight is currently off-screen.
                      const currentTop = container.scrollTop;
                      const currentBottom = currentTop + containerH;
                      if (targetY < currentTop || targetY > currentBottom - padding) {
                        container.scrollTo({ top: desiredScrollTop, behavior: 'smooth' });
                      }
                    }}
                  />
                ))}
            </div>
          </Document>
        </div>
      </div>

      {/* Info footer — keyboard shortcut hint only. Match-confidence used to
          live here as a misleading green banner; it now lives in the sidebar
          pill where it can reflect actual match status. */}
      <div className="p-2 bg-gray-50 border-t text-xs text-gray-500">
        <div className="flex items-center justify-end">
          {citations.length > 1 && (
            <div className="text-gray-600">
              <span className="font-medium">Keyboard:</span> Ctrl+←→ to navigate citations
            </div>
          )}
        </div>

        {/* Citation mini-map for current page */}
        {totalCitationsOnPage > 1 && (
          <div className="mt-2 pt-2 border-t border-gray-200">
            <div className="flex items-center gap-2">
              <span className="text-gray-600">Citations on this page:</span>
              <div className="flex gap-1">
                {citationsOnCurrentPage.map((_, idx) => {
                  const globalIdx = citations.findIndex(c => c === citationsOnCurrentPage[idx]);
                  return (
                    <button
                      key={idx}
                      onClick={() => navigateToCitation(globalIdx)}
                      className={`w-6 h-6 rounded text-xs font-medium transition-colors ${
                        globalIdx === currentCitationIndex
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-300 text-gray-700 hover:bg-gray-400'
                      }`}
                      title={`Citation ${globalIdx + 1}`}
                    >
                      {globalIdx + 1}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HighlightedPdfViewer;